/**
 * `useWikiSearch` — title + body-content search across the tenant's wiki
 * nodes.
 *
 * Wraps `GET /api/wiki/search?q=<string>&limit=<int>&kinds=<csv>`. Backs
 * the wikilink slash-menu picker (Wave C3) and the global Cmd-K palette's
 * Wiki tab (Phase 3d + Phase 5 Wave 2 — body-content match).
 *
 * Hook contract:
 *   - Skips the network call when `query.length === 0` — the BE Zod
 *     schema requires `q.min(1)`, so the empty-query case would 400.
 *     The consumer's debounced input drives `query`; this hook just
 *     mirrors whatever the consumer passes.
 *   - `staleTime: 30_000` keeps rapid keystrokes from storming the BE
 *     when the user erases + retypes. The result set is fundamentally
 *     ephemeral, so a 30s cache is enough; we never want to make this
 *     queryFn rebuild the picker UI on every keystroke if the typed
 *     substring round-trips back to a previously-queried value.
 *   - Each result row is a `WikiNode` extended with `snippet: string |
 *     null`. Title-tier matches set `snippet: null`; body-tier matches
 *     carry an HTML fragment with `<mark>...</mark>` brackets around
 *     every highlighted token (server-rendered via `ts_headline`).
 *     Consumers needing only the `WikiNode` shape (the wikilink picker)
 *     can read the row as a `WikiNode` directly — TypeScript widening
 *     handles the structural compatibility.
 *
 * BE wire shape (verified against `3mote-be-new/src/wiki/router.ts`
 * `searchRoute` / `searchWiki`):
 *
 *   200 OK { data: { results: Array<WikiNode & { snippet: string | null }> } }
 *
 * The handler excludes soft-deleted rows (`isNull(deletedAt)`) so a
 * picker result never references a node the user can't open. Broken-
 * link treatment on the chip itself fires when a previously-inserted
 * wikilink's target has since been deleted — that resolution happens
 * at render time against `useWikiTree().nodesById`, not via this hook.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { apiClient } from '@/api/client'
import type { WikiNode, WikiNodeKind } from '@/api/types-bridge'

import { wikiSearchQueryKey } from './query-keys'
import { useWikiTree } from './use-wiki-tree'

/**
 * Subset of `WikiNodeKind` that's meaningful as a wikilink target.
 * Callers default to `['doc', 'folder']`; passing a different filter
 * (e.g. `['doc']` only) is supported but uncommon.
 */
export type WikiSearchKindFilter = readonly WikiNodeKind[]

export interface UseWikiSearchOptions {
  /**
   * Restrict results to specific node kinds. The BE accepts a CSV
   * (`kinds=doc,folder`); omitting the param means "every kind in
   * scope". Default for the wikilink picker is `['doc', 'folder']`.
   */
  kinds?: WikiSearchKindFilter
  /**
   * Maximum number of hits to return. BE clamps to `[1, 50]`; default
   * is 20. The picker UI shows ~7 rows visible at a time, so the
   * default fits the screen without scrolling for most queries.
   */
  limit?: number
}

/**
 * One row in the search response. `snippet` is `null` for title-tier
 * matches (the FE renders name + breadcrumb only) and a `<mark>`-
 * bracketed HTML fragment for body-tier matches (Phase 5 Wave 2).
 *
 * The base shape is `WikiNode` — every consumer that previously read a
 * `WikiNode[]` from this hook continues to work; the `snippet` field is
 * additive.
 */
export type WikiSearchResultRow = WikiNode & {
  snippet: string | null
}

export interface UseWikiSearchResult {
  /** The flat list of matching rows. Empty array when query is empty / loading / errored. */
  data: WikiSearchResultRow[]
  isLoading: boolean
  isError: boolean
}

const WIKI_SEARCH_STALE_TIME_MS = 30_000
const DEFAULT_EMPTY_QUERY_LIMIT = 20

export function useWikiSearch(
  query: string,
  options: UseWikiSearchOptions = {},
): UseWikiSearchResult {
  const kinds = options.kinds
  const limit = options.limit

  const trimmedQuery = query.trim()
  const enabled = trimmedQuery.length > 0

  // Empty-query fallback — when the picker first opens the user hasn't
  // typed anything yet. Confluence / Notion behavior: show every available
  // page sorted by name (capped to ~20) so the user can scroll-pick a
  // recent page without having to remember its name. We source these from
  // the already-loaded `useWikiTree().nodesById` map (no extra BE call;
  // the tree query is the canonical "every node the user can see" cache).
  // Empty-query rows carry `snippet: null` — no FTS run, nothing to highlight.
  const tree = useWikiTree()
  const emptyQueryResults = useMemo<WikiSearchResultRow[]>(() => {
    if (enabled) return []
    const all = Array.from(tree.nodesById.values())
    const filtered = kinds && kinds.length > 0 ? all.filter((n) => kinds.includes(n.kind)) : all
    filtered.sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }),
    )
    const cap = typeof limit === 'number' ? limit : DEFAULT_EMPTY_QUERY_LIMIT
    return filtered.slice(0, cap).map((n) => ({ ...n, snippet: null }))
  }, [enabled, tree.nodesById, kinds, limit])

  const queryResult = useQuery({
    queryKey: wikiSearchQueryKey(trimmedQuery, kinds, limit),
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('q', trimmedQuery)
      if (kinds && kinds.length > 0) params.set('kinds', kinds.join(','))
      if (typeof limit === 'number') params.set('limit', String(limit))
      const res = await apiClient<{ data: { results: WikiSearchResultRow[] } }>(
        `/api/wiki/search?${params.toString()}`,
      )
      // Defense against an older BE that omits `snippet` from the wire
      // shape — coerce missing/undefined to `null` so the FE consumers
      // never have to branch on "shape might be old."
      return res.data.results.map((r) => ({ ...r, snippet: r.snippet ?? null }))
    },
    enabled,
    staleTime: WIKI_SEARCH_STALE_TIME_MS,
  })

  return {
    data: enabled ? (queryResult.data ?? []) : emptyQueryResults,
    isLoading: queryResult.isLoading && enabled,
    isError: queryResult.isError,
  }
}
